import { WorklogStore, type WorklogEntry } from "@vortex-os/worklog";
import { DecisionStore, type DecisionEntry } from "@vortex-os/decision-log";
import type { ModuleContext } from "@vortex-os/core";

/**
 * "What should I do today?" — a read-only synthesis over existing records
 * (worklog + decision-log + open `- [ ]` checkboxes), the P2 work-management
 * surface. No new store: it reads what's already written and answers
 * "what were you doing, what's open, what's next" from it.
 *
 * Design goal (operator, 2026-05-30): adapt to EVERY data state — a brand-new
 * user with nothing, a partly-used instance, and a rich history must each get a
 * sensible report. So the collector returns a structured, presence-flagged
 * shape and the renderer branches on what's actually there rather than assuming
 * any section exists.
 */

/** An unchecked `- [ ]` item lifted from a recent worklog body. */
export interface OpenTask {
  readonly text: string;
  /** Worklog date the task was found in (`YYYY-MM-DD`). */
  readonly fromDate: string;
}

export interface OpenDecision {
  readonly title: string;
  readonly date: string;
  readonly slug: string;
}

export interface AgendaReport {
  /** Most recent worklog (what you were last doing), or null if none. */
  readonly lastWorklog: { readonly date: string; readonly title: string; readonly path: string } | null;
  /**
   * "Next up" lines lifted from the most recent worklog's hand-off section
   * (`## Next` / a `📋`-marked heading) — the planned-work pointer, mirroring
   * a worklog hand-off block. Empty if none.
   */
  readonly nextUp: readonly string[];
  /** Date of the worklog the nextUp lines came from (or null). */
  readonly nextUpFrom: string | null;
  /** Unchecked checkboxes from recent worklogs, newest first, capped. */
  readonly openTasks: readonly OpenTask[];
  /** Active (non-archived) decisions, newest first, capped. */
  readonly openDecisions: readonly OpenDecision[];
  /** Total worklog / decision counts — used to distinguish "new" from "quiet". */
  readonly worklogCount: number;
  readonly decisionCount: number;
  /** True when there is essentially nothing yet (brand-new instance). */
  readonly isEmpty: boolean;
  /** True when there are records but nothing actionable surfaced. */
  readonly nothingOpen: boolean;
}

export interface CollectAgendaOptions {
  /** How many recent worklogs to scan for open tasks. Default 7. */
  readonly recentWorklogs?: number;
  /** Max open tasks / decisions to surface. Default 8 each. */
  readonly maxTasks?: number;
  readonly maxDecisions?: number;
}

const DEFAULT_RECENT = 7;
const DEFAULT_MAX = 8;

/** First `# ` heading of a worklog body, else its keyword/filename stem. */
function worklogTitle(entry: WorklogEntry): string {
  const m = entry.body.match(/^#\s+(.+)$/m);
  if (m) return m[1]!.trim();
  return entry.keyword || entry.date;
}

/**
 * Pull unchecked GitHub-style checkboxes (`- [ ]` / `* [ ]`, any indent) from a
 * worklog body. Checked items (`- [x]`) are ignored. Returns the trimmed label.
 */
/**
 * Lift the "next up / hand-off" section from a worklog body — the planned-work
 * pointer the agent leaves at wind-down (mirrors a worklog hand-off block).
 * Matches a heading whose text contains any of the cue terms (English and
 * Korean: "next", "next up", "todo", "다음 작업", "다음 세션", "후속", "📋").
 * Returns the section's content lines (bullets de-marked, blanks dropped),
 * capped. Empty if no such section. Stops at the next heading of the
 * same-or-higher level. A CHECKED checkbox (`- [x]`) under the heading is
 * completed work, not "next up", so it is skipped; an unchecked `- [ ]` and
 * plain bullets/prose are kept.
 */
export function extractNextUp(body: string, max = 8): string[] {
  const lines = body.split(/\r?\n/);
  const headingRe = /^(#{1,6})\s+(.*)$/;
  const cueRe = /(다음\s*작업|다음\s*세션|후속|next\s*up|next|todo|to-do|📋)/i;
  let collecting = false;
  let startLevel = 0;
  const out: string[] = [];
  for (const line of lines) {
    const h = line.match(headingRe);
    if (h) {
      const level = h[1]!.length;
      if (collecting && level <= startLevel) break; // end of section
      if (!collecting && cueRe.test(h[2]!)) {
        collecting = true;
        startLevel = level;
        continue;
      }
      continue;
    }
    if (!collecting) continue;
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    if (trimmed.startsWith(">")) continue; // skip callout/quote framing
    // A checkbox carries completion state: a CHECKED item (`[x]`/`[X]`) is done
    // work, not "next up" — skip it. An unchecked `[ ]` is pending → keep it
    // (de-marked below). The marker may follow a bullet (`- [x]`) OR a numbered
    // prefix (`1. [x]`), and may have NO trailing label (`- [x]`). Non-checkbox
    // bullets/prose are unaffected.
    const checkbox = trimmed.match(/^(?:[-*]|\d+[.)])\s+\[([ xX])\](?:\s+|$)/);
    if (checkbox && checkbox[1] !== " ") continue;
    // De-mark common bullet / numbered / checkbox prefixes for a clean line.
    // A bare checkbox (`- [ ]`, no label) de-marks to empty and is dropped by
    // the `cleaned.length === 0` guard below.
    const cleaned = trimmed
      .replace(/^(?:[-*]|\d+[.)])\s+\[[ xX]\](?:\s+|$)/, "")
      .replace(/^[-*]\s+/, "")
      .replace(/^\d+[.)]\s+/, "")
      .trim();
    if (cleaned.length === 0) continue;
    out.push(cleaned);
    if (out.length >= max) break;
  }
  return out;
}

/**
 * Pull unchecked GitHub-style checkboxes (`- [ ]` / `* [ ]`, any indent) from a
 * worklog body. Checked items (`- [x]`) are ignored. Returns the trimmed label.
 */
export function extractOpenTasks(body: string): string[] {
  const out: string[] = [];
  for (const line of body.split(/\r?\n/)) {
    const m = line.match(/^\s*(?:[-*]|\d+[.)])\s+\[\s\]\s+(.+\S)\s*$/);
    if (m) out.push(m[1]!.trim());
  }
  return out;
}

/**
 * Merge the "next up" pointers of SEVERAL worklogs (e.g. every entry sharing the
 * latest day) into one bounded, fair list — so a day with multiple topic/session
 * worklogs doesn't bury all but one hand-off (the old "pick the single
 * lexically-greatest file" bug). Per worklog the hand-off section
 * (`extractNextUp`) is preferred; with `fallbackToOpenTasks` (default on) a
 * worklog lacking a `## Next`-style heading contributes its own unchecked tasks
 * instead, so it still surfaces. The per-worklog queues are merged ROUND-ROBIN
 * (one item from each, then the seconds, …) up to `maxTotal`, so every worklog
 * gets its top step in before any gets a second. Order across worklogs follows
 * the input order — callers pass a deterministic (path-sorted) group.
 *
 * `fallbackToOpenTasks: false` is for callers (like `/agenda`) that already list
 * open tasks separately, to avoid showing the same task twice.
 */
export function aggregateHandoff(
  bodies: readonly string[],
  maxTotal: number,
  opts?: { readonly fallbackToOpenTasks?: boolean },
): string[] {
  if (maxTotal <= 0) return [];
  const fallback = opts?.fallbackToOpenTasks ?? true;
  const queues = bodies.map((b) => {
    const nu = extractNextUp(b, maxTotal);
    if (nu.length > 0) return nu;
    return fallback ? extractOpenTasks(b) : [];
  });
  const out: string[] = [];
  const seen = new Set<string>(); // a step copied into two same-day worklogs surfaces once
  const rounds = queues.reduce((m, q) => Math.max(m, q.length), 0);
  for (let i = 0; i < rounds && out.length < maxTotal; i++) {
    for (const q of queues) {
      if (i < q.length) {
        const item = q[i]!;
        if (seen.has(item)) continue;
        seen.add(item);
        out.push(item);
        if (out.length >= maxTotal) break;
      }
    }
  }
  return out;
}

/**
 * Collect the agenda inputs. Read-only; tolerant of missing dirs (both stores
 * return empty rather than throwing), so a brand-new instance yields an empty —
 * but well-formed — report.
 */
export async function collectAgenda(
  ctx: ModuleContext,
  opts?: CollectAgendaOptions,
): Promise<AgendaReport> {
  const recentN = opts?.recentWorklogs ?? DEFAULT_RECENT;
  const maxTasks = opts?.maxTasks ?? DEFAULT_MAX;
  const maxDecisions = opts?.maxDecisions ?? DEFAULT_MAX;

  // WorklogStore's root is the worklog/ folder itself (it walks YYYY/MM under it);
  // DecisionStore's root is the decision-log/ folder. Both live under dataDir.
  const worklogStore = new WorklogStore(`${ctx.dataDir}/worklog`);
  const decisionStore = new DecisionStore(joinDecisionRoot(ctx));

  const allWorklogs = await worklogStore.list();
  // `list()` order is not guaranteed sorted; sort by date desc for "recent".
  const sortedWorklogs = [...allWorklogs].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const recent = sortedWorklogs.slice(0, recentN);

  const lastWorklog = sortedWorklogs[0]
    ? { date: sortedWorklogs[0].date, title: worklogTitle(sortedWorklogs[0]), path: sortedWorklogs[0].path }
    : null;

  const openTasks: OpenTask[] = [];
  for (const wl of recent) {
    for (const text of extractOpenTasks(wl.body)) {
      openTasks.push({ text, fromDate: wl.date });
      if (openTasks.length >= maxTasks) break;
    }
    if (openTasks.length >= maxTasks) break;
  }

  // Planned work: the hand-off of the most recent DAY — aggregated across ALL
  // worklogs sharing that latest date (a day can hold several topic/session
  // worklogs), so no same-day hand-off is buried behind another. Older days are
  // superseded. Sort the day's worklogs by path so the round-robin order matches
  // the session-start report (which sorts its same-day group the same way).
  // Don't fall back to open tasks here (the open-tasks list below covers those);
  // any `- [ ]` that IS in a cue section is de-duplicated out of that list.
  const newest = sortedWorklogs[0];
  const latestDay = newest
    ? sortedWorklogs
        .filter((w) => w.date === newest.date)
        .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0))
    : [];
  const nextUp = aggregateHandoff(
    latestDay.map((w) => w.body),
    maxTasks,
    { fallbackToOpenTasks: false },
  );
  const nextUpFrom = newest && nextUp.length > 0 ? newest.date : null;

  // A hand-off step written as `- [ ]` under a `## Next` cue is BOTH a next-up
  // line and an open task — surface it once (under next-up), not twice.
  const nextUpSet = new Set(nextUp);
  const visibleOpenTasks = openTasks.filter((t) => !nextUpSet.has(t.text));

  const allDecisions = await decisionStore.list();
  const active = allDecisions.filter((d: DecisionEntry) => {
    const s = (d.frontmatter?.status ?? "active").toLowerCase();
    return s !== "archived" && s !== "template";
  });
  const sortedDecisions = [...active].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const openDecisions: OpenDecision[] = sortedDecisions.slice(0, maxDecisions).map((d) => ({
    title: decisionTitle(d),
    date: d.date,
    slug: d.slug,
  }));

  const worklogCount = allWorklogs.length;
  const decisionCount = allDecisions.length;
  const isEmpty = worklogCount === 0 && decisionCount === 0;
  const nothingOpen =
    !isEmpty && visibleOpenTasks.length === 0 && openDecisions.length === 0 && nextUp.length === 0;

  return {
    lastWorklog,
    nextUp,
    nextUpFrom,
    openTasks: visibleOpenTasks,
    openDecisions,
    worklogCount,
    decisionCount,
    isEmpty,
    nothingOpen,
  };
}

function joinDecisionRoot(ctx: ModuleContext): string {
  // DecisionStore takes the decision-log root directly.
  return `${ctx.dataDir}/decision-log`;
}

function decisionTitle(d: DecisionEntry): string {
  const m = (d.body ?? "").match(/^#\s+(.+)$/m);
  if (m) return m[1]!.trim();
  return d.slug;
}

/**
 * Render the agenda as a compact markdown block. Branches on data state so the
 * output is sensible whether the instance is brand-new, quiet, or busy:
 *   - brand-new (no records)     → a short "getting started" nudge
 *   - records but nothing open   → "you're clear" + last activity
 *   - open tasks / decisions     → an actionable list
 */
/**
 * Neutralize worklog-derived text before it enters the rendered agenda (which
 * the agent reads as context): strip angle brackets `<`/`>` → space so a crafted
 * worklog can't forge a framing/section delimiter, plus control bytes, and
 * collapse whitespace. Mirrors the session-start report's neutralizer; /agenda
 * is a lower-trust on-demand surface, but it's the same untrusted-content path.
 */
function neutralizeAgendaText(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/[<>]/g, " ").replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
}

export function renderAgenda(report: AgendaReport): string {
  const lines: string[] = ["## What should I do today?", ""];

  if (report.isEmpty) {
    lines.push("- No worklog or decisions yet — this looks like a fresh instance.");
    lines.push("- Start with `/vortex init` (if you haven't), then `/log <one-line update>` as you work.");
    lines.push("- A worklog entry per working day is the seed; everything else grows from it.");
    return lines.join("\n") + "\n";
  }

  if (report.lastWorklog) {
    lines.push(`- last active: ${report.lastWorklog.date} — ${neutralizeAgendaText(report.lastWorklog.title)}`);
  }

  // Planned work first — it's the explicit hand-off of "what's next".
  if (report.nextUp.length > 0) {
    lines.push(`- next up (planned, from ${report.nextUpFrom}):`);
    for (const n of report.nextUp) {
      lines.push(`  - ${neutralizeAgendaText(n)}`);
    }
  }

  if (report.openTasks.length > 0) {
    lines.push(`- open tasks (${report.openTasks.length}):`);
    for (const t of report.openTasks) {
      lines.push(`  - [ ] ${neutralizeAgendaText(t.text)}  (${t.fromDate})`);
    }
  }

  if (report.openDecisions.length > 0) {
    lines.push(`- open decisions (${report.openDecisions.length}):`);
    for (const d of report.openDecisions) {
      lines.push(`  - ${neutralizeAgendaText(d.title)}  (${d.date})`);
    }
  }

  if (report.nothingOpen) {
    lines.push(
      `- nothing open in recent worklogs — you're clear. ${report.worklogCount} worklog(s), ${report.decisionCount} decision(s) on record.`,
    );
    lines.push(
      "- Leave a `## Next` section in a worklog, or `- [ ] <task>` lines, to have them surface here next time.",
    );
  }

  return lines.join("\n") + "\n";
}
